import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "./_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Area, AreaChart, Legend } from "recharts";
import { DollarSign, TrendingUp, ShoppingCart, Percent } from "lucide-react";

export default function Financial() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/financial-forecast").then((r) => setData(r.data)); }, []);

  if (!data) return <Skeleton className="h-96" />;

  return (
    <Page title="Previsão financeira" subtitle="Projeção de receita, lucro e investimento para os próximos 30 dias.">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <Card icon={DollarSign} label="Receita projetada" value={formatBRL(data.expected_revenue)} accent />
        <Card icon={TrendingUp} label="Lucro projetado" value={formatBRL(data.expected_profit)} hint={`Margem ${data.margin_pct}%`} />
        <Card icon={ShoppingCart} label="Investimento em compras" value={formatBRL(data.purchase_investment)} />
        <Card icon={Percent} label="Margem bruta" value={`${data.margin_pct}%`} />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card mt-6">
        <h3 className="font-display text-lg font-semibold mb-4">Receita & lucro diário (30 dias)</h3>
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={data.daily}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F05A3F" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#F05A3F" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34D399" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1A2B47" strokeDasharray="3 3" />
            <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
            <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} formatter={(v) => formatBRL(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="revenue" name="Receita" stroke="#F05A3F" fill="url(#rev)" strokeWidth={2.5} />
            <Area type="monotone" dataKey="profit" name="Lucro" stroke="#34D399" fill="url(#prof)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Page>
  );
}

const Card = ({ icon: Icon, label, value, hint, accent }) => (
  <div className={`p-6 rounded-xl border bg-card ${accent ? "border-primary/40 glow-coral" : "border-border"}`}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">{label}<Icon className="w-4 h-4" /></div>
    <div className={`font-display text-3xl font-bold mt-3 ${accent ? "text-primary" : ""}`}>{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
  </div>
);
